import { Circle, Flex, Text } from "@chakra-ui/react";
import { handleClientError } from "lib/errors";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { type BrandColorToken } from "theme/colors";

type StatusIndicator = "none" | "minor" | "major" | "critical";

type StatusResponse = {
  page: {
    id: string;
    name: string;
    url: string;
    updated_at: string;
  };
  status: {
    description: string;
    indicator: StatusIndicator;
  };
};

const loadingText = "Loading status...";
const defaultText = "All systems normal";
const incidentText = "Certain services degraded";

/**
 * Displays current status of Hightouch app.
 * @source https://status.hightouch.io/api/v2
 */
export function HightouchStatus() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusIndicator | undefined>();

  let displayText = defaultText;
  let primaryColor: BrandColorToken = "new.gray.500";

  switch (status) {
    case "none": {
      primaryColor = "new.green.300";
      displayText = defaultText;
      break;
    }

    case "minor": {
      primaryColor = "new.yellow.300";
      displayText = incidentText;
      break;
    }

    case "major": {
      primaryColor = "new.yellow.500";
      displayText = incidentText;
      break;
    }

    case "critical": {
      primaryColor = "new.yellow.500";
      displayText = incidentText;
      break;
    }

    // No default
    default: {
      break;
    }
  }

  async function getStatus() {
    try {
      setLoading(true);
      const response = await fetch("https://status.hightouch.io/api/v2/summary.json");
      if (!response.ok) {
        handleClientError(`Failed to fetch status page data: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status.indicator) {
        setStatus(data.status.indicator);
      }
    } catch (error) {
      handleClientError(`Error fetching status page data: ${error}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void getStatus();
  }, []);

  return (
    <Flex
      as={Link}
      href="https://status.hightouch.io"
      align="center"
      bg="transparent"
      borderRadius="smallest"
      gap="tiny"
      py="tiny"
      px="smallest"
      // Offset left padding for alignment
      ml="calc(var(--chakra-space-smallest) * -1)"
      userSelect="none"
      transition="background-color 0.15s ease-out"
      _hover={{
        bg: "rgba(255, 255, 255, 0.04)",
      }}
    >
      <Circle size="tiny" bg={primaryColor} />
      <Text textStyle="caption2">{loading ? loadingText : displayText}</Text>
    </Flex>
  );
}
